/**
 * i18next setup — this is the "language" leg of multi-language/currency/country support.
 * Deliberately kept separate from utils/locale.ts (currency/country/tax settings): the UI
 * language is a per-user, purely client-side preference (persisted by
 * i18next-browser-languagedetector under its own localStorage key, 'i18nextLng') that works
 * even before login / before this deployment's backend localization settings have loaded —
 * e.g. on the Login screen itself. `resources` below only covers the translation keys that
 * have actually been wired into components so far (Sidebar nav + common chrome + the
 * Localization settings panel — see components/layout/Sidebar.tsx and SettingsPage.tsx).
 *
 * Extending coverage to the rest of the app follows the same pattern used there:
 *   const { t } = useTranslation();
 *   t('nav.someKey', 'English fallback text')
 * The fallback (2nd arg) means an untranslated key never renders as a raw "nav.someKey" key —
 * it just shows the English text until someone adds a translation for it, so this can be
 * extended incrementally without ever breaking a screen that hasn't been touched yet.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './en.json';
import sw from './sw.json';
import fr from './fr.json';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en }, sw: { translation: sw }, fr: { translation: fr } },
    fallbackLng: 'en',
    // Every language offered anywhere in the UI (the header switcher, and Settings >
    // Localization's Language dropdown, which also offers French for the DR Congo/Rwanda/
    // Burundi/Senegal country presets) MUST be listed here. i18next's changeLanguage() treats
    // supportedLngs as an allowlist — calling changeLanguage('fr') while 'fr' isn't listed
    // silently falls back to fallbackLng ('en') instead of erroring, which is exactly why
    // picking "Français" and saving previously left the UI in English with no visible failure.
    supportedLngs: ['en', 'sw', 'fr'],
    interpolation: { escapeValue: false },
    detection: { order: ['localStorage', 'navigator'], caches: ['localStorage'] },
  });

export default i18n;

export const SUPPORTED_LANGUAGES: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'sw', label: 'Kiswahili' },
  { code: 'fr', label: 'Français' },
];
